/**
 * The search + status toolbar of the extranet lists, factored once (fiches
 * 22986 Contrats / 22987 Flotte / 22988 Primes / 22989 Sinistres): a
 * declarative DataList {@link ToolbarConfig} — the single search box
 * (placeholder in config) and, optionally, one status select fed by a
 * `WS2_EA_SEL_*` endpoint (stable filter id, label, « Tous … » option in
 * config) — so the tenant rules reach it through the `search` / `filters`
 * buckets (`setSearchPlaceholder`, `hideSearch`, `renameFilter`,
 * `hideFilter`, …).
 *
 * Both are server-side on the four lists: an empty search or the « Tous … »
 * option (value {@link ALL_OPTION_VALUE}) means the parameter is **not sent**.
 * Contrats lets DataList build the request (endpoint mode, `paramName`s);
 * Primes / Sinistres / Flotte load their rows through their page, which
 * receives the toolbar state as a {@link ListToolbarQuery} from
 * `DataTableCard` (`onToolbarChange`) and maps it to the VEOS parameters in
 * its service (`build*ListParams`).
 */

import type { FilterConfig, ToolbarConfig } from '@components/integrated/data-list';

import type { SelOption } from '../../api/listFilters';

/** Debounce of the list search boxes, in ms (one request once typing pauses). */
export const LIST_SEARCH_DEBOUNCE_MS = 300;

/** Value of the « Tous … » / « Toutes … » option: empty ⇒ the parameter is not sent. */
export const ALL_OPTION_VALUE = '';

/** The search box of a list toolbar. */
export interface ListSearchSpec {
  /** Placeholder (e.g. « Rechercher par réf, produit, assuré… »). */
  placeholder: string;
  /** VEOS parameter — only read in DataList endpoint mode (it builds the request). */
  paramName?: string;
}

/** The optional status select of a list toolbar. */
export interface ListStatusFilterSpec {
  /** Stable filter id — the rule key of the `filters` bucket. */
  id: string;
  /** Filter label — also the select placeholder, so a `renameFilter` rule reaches it. */
  label: string;
  /** Label of the empty « all » option (« Tous statuts », « Toutes situations », …). */
  allLabel: string;
  /** Position of the « all » option: `'first'` (default) or `'last'` (Contrats). */
  allPosition?: 'first' | 'last';
  /** The `WS2_EA_SEL_*` options (`VAL` / `LIB`), API order. */
  options: readonly SelOption[];
  /** Option selected on load — default {@link ALL_OPTION_VALUE} (« all »). */
  defaultValue?: string;
  /** VEOS parameter — only read in DataList endpoint mode. */
  paramName?: string;
}

/** A list toolbar: the search box and, optionally, the status select. */
export interface ListToolbarSpec {
  search: ListSearchSpec;
  status?: ListStatusFilterSpec;
}

/**
 * State of a list toolbar as reported to the page that loads the rows
 * (`DataTableCard` `onToolbarChange`): the debounced, trimmed search text
 * (`''` when empty) and the value of every filter keyed by its stable id
 * (`''` for « Tous … »).
 */
export interface ListToolbarQuery {
  search: string;
  filters: Readonly<Record<string, string>>;
}

/** The select options: the SEL options (API order) and the « all » option first or last. */
export function statusFilterOptions(
  options: readonly SelOption[],
  allLabel: string,
  allPosition: 'first' | 'last' = 'first',
): SelOption[] {
  const all: SelOption = { value: ALL_OPTION_VALUE, label: allLabel };
  const sel = options.map((option) => ({ value: option.value, label: option.label }));
  return allPosition === 'first' ? [all, ...sel] : [...sel, all];
}

/** The status select of a list toolbar (`type: 'select'`, order 1). */
export function buildStatusFilter(spec: ListStatusFilterSpec): FilterConfig {
  return {
    id: spec.id,
    // No explicit placeholder: the (overridable) label is the select's
    // placeholder, so a `renameFilter` rule reaches the toolbar.
    label: spec.label,
    type: 'select',
    ...(spec.paramName === undefined ? {} : { paramName: spec.paramName }),
    options: statusFilterOptions(spec.options, spec.allLabel, spec.allPosition),
    defaultValue: spec.defaultValue ?? ALL_OPTION_VALUE,
    order: 1,
  };
}

/** The DataList toolbar of a list: server-side search (debounced) + optional status select. */
export function buildListToolbar({ search, status }: ListToolbarSpec): ToolbarConfig {
  return {
    visible: true,
    search: {
      enabled: true,
      mode: 'server',
      ...(search.paramName === undefined ? {} : { paramName: search.paramName }),
      placeholder: search.placeholder,
      debounceMs: LIST_SEARCH_DEBOUNCE_MS,
    },
    ...(status === undefined ? {} : { filters: [buildStatusFilter(status)] }),
  };
}

/**
 * The filter values of a toolbar as a {@link ListToolbarQuery} `filters`
 * record: one entry per configured filter (config order), its current value
 * as a string — `''` when unset or not a string.
 */
export function toolbarFilterValues(
  filters: readonly FilterConfig[] | undefined,
  values: Readonly<Record<string, unknown>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const filter of filters ?? []) {
    const value = values[filter.id];
    out[filter.id] = typeof value === 'string' ? value : ALL_OPTION_VALUE;
  }
  return out;
}
